import { useCallback, useContext, useEffect, useRef, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DataGridPro,
  GridColDef,
  GridColumnHeaderParams,
  GridHeaderCheckbox,
  GridListViewColDef,
  GridRenderCellParams,
  GridRowId,
  GridRowParams,
  GridRowSelectionModel,
  GridValidRowModel,
  Toolbar,
} from '@mui/x-data-grid-pro';

import { ctxReport } from '@/contexts/ReportCtx';
import { getAvailableReports } from '@/generated/client/reports/reports';
import { ReportTemplateRequestDto, ReportTemplateResponseDto } from '@/generated/model';
import { SearchComponent } from '../common/Search';
import { ctxPopup } from '@/contexts/PopupContext';
import { isEqual } from 'lodash';
import { Avatar, Box, Checkbox, Stack, Typography } from '@mui/material';
import { ToolbarSortItem } from '@/components/common/ToolbarSortItem';
import ReportEmailButton from './ReportEmailButton';
import SelectionCounter from './SelectionCounter';

export function ReportData({ isListView }: { isListView: boolean }) {
  const { t } = useTranslation();
  const reportCtx = useContext(ctxReport);
  const popupCtx = useContext(ctxPopup);
  const [searchText, setSearchText] = useState<string | undefined>();
  const [reportTemplates, setReportTemplates] = useState<ReportTemplateResponseDto[]>([]);
  const [filteredTemplates, setFilteredTemplates] = useState<ReportTemplateResponseDto[]>([]);
  const dataRef = useRef<HTMLDivElement | null>(null);
  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>({
    type: 'include',
    ids: new Set<GridRowId>(),
  });

  const request = useMemo<ReportTemplateRequestDto | null>(() => {
    const selectedTargets = reportCtx?.selectedTargets;
    const selectedDrivers = reportCtx?.selectedDrivers;
    const selectedDates = reportCtx?.selectedDates;

    if (!selectedTargets?.length && !selectedDrivers?.length) return null;

    const targetIds = selectedTargets?.map((target) => target.id);
    const driverIds = selectedDrivers?.map((driver) => driver.id);

    let fromTimeMicros: number | undefined;
    let toTimeMicros: number | undefined;

    if (selectedDates) {
      fromTimeMicros = selectedDates.from.getTime() * 1000;
      toTimeMicros = selectedDates.to.getTime() * 1000;
    }

    return { targetIds, driverIds, fromTimeMicros, toTimeMicros };
  }, [reportCtx?.selectedTargets, reportCtx?.selectedDrivers, reportCtx?.selectedDates]);

  useEffect(() => {
    if (!isListView) {
      dataRef.current!.style.width = `calc(100vw - ${popupCtx?.drawerMenuOpen ? 740 : 558}px)`;
    }
  }, [popupCtx?.drawerMenuOpen]);

  useEffect(() => {
    if (!searchText) {
      setFilteredTemplates(reportTemplates);
    } else {
      const filtered = reportTemplates.filter((row) =>
        Object.values(row).some((field) => field.toString().toLowerCase().indexOf(searchText.toLowerCase()) > -1)
      );
      setFilteredTemplates(filtered);
    }
  }, [searchText, reportTemplates]);

  const updateReportTemplates = useCallback(
    async (request: ReportTemplateRequestDto) => {
      const response = await getAvailableReports(request);
      // TODO error handling
      const newTemplates: ReportTemplateResponseDto[] = response.data;
      setReportTemplates((prev) => {
        if (isEqual(prev, newTemplates)) {
          return prev;
        }
        if (!searchText) {
          setFilteredTemplates(newTemplates);
        } else {
          const filtered = newTemplates.filter((row) =>
            Object.values(row).some((field) => field.toString().toLowerCase().indexOf(searchText.toLowerCase()) > -1)
          );
          setFilteredTemplates(filtered);
        }
        return newTemplates;
      });
    },
    [searchText]
  );

  useEffect(() => {
    if (!request) {
      setReportTemplates([]);
      setFilteredTemplates([]);
      return;
    }
    void updateReportTemplates(request);
  }, [request, updateReportTemplates]);

  useEffect(() => {
    const ids = Array.from(selectionModel.ids);
    const numIds = ids.map((id) => (typeof id === 'string' ? parseInt(id, 10) : id));
    reportCtx?.setSelectedTemplateIds(numIds);
  }, [selectionModel]);

  if (!reportCtx) return null;

  const columns: GridColDef<ReportTemplateResponseDto>[] = [
    {
      field: '__check__',
      type: 'custom',
      sortable: false,
      width: 90,
      resizable: false,
      headerName: '',
      renderHeader: (params: GridColumnHeaderParams<ReportTemplateResponseDto>) => (
        <Box display="inline-flex" alignItems="center" alignContent="start" p={0} width="100px">
          <GridHeaderCheckbox {...(params as unknown as GridColumnHeaderParams<GridValidRowModel>)} />
          <SelectionCounter selectionModel={selectionModel} allCount={reportTemplates.length} />
        </Box>
      ),
    },
    {
      field: 'id',
      headerName: t('report.table.column.id'),
      flex: 1,
    },
    {
      field: 'name',
      headerName: t('report.table.column.name'),
      flex: 1,
    },
    {
      field: 'code',
      headerName: t('report.table.column.code'),
      flex: 1,
    },
  ];

  function getNoRowsLabel() {
    const selectedTargets = reportCtx?.selectedTargets;
    const selectedDrivers = reportCtx?.selectedDrivers;
    if ((!selectedTargets || selectedTargets.length === 0) && (!selectedDrivers || selectedDrivers.length === 0)) {
      return t('report.table.noEntitySelected');
    }
    return t('filter.noRows');
  }

  function handleTemplateSelection(templateId: number) {
    reportCtx?.setSelectedTemplateIds((prev) =>
      prev.includes(templateId) ? prev.filter((rowId) => rowId !== templateId) : [...prev, templateId]
    );
  }

  function ListViewCell(params: GridRenderCellParams<ReportTemplateResponseDto>) {
    const isChecked = reportCtx?.selectedTemplateIds.includes(params.row.id);

    return (
      <Stack
        direction="row"
        sx={{
          width: '100%',
          alignItems: 'center',
          height: '100%',
          gap: 2,
          paddingY: 1,
        }}
        className={`${isChecked ? 'selected-report-row' : ''}`}
      >
        <Checkbox
          checked={isChecked}
          onChange={() => handleTemplateSelection(params.row.id)}
          sx={{ marginLeft: '5px' }}
        />

        <Avatar className="report-data-avatar" variant="rounded">
          {params.row.id.toString().toUpperCase()}
        </Avatar>

        <Stack sx={{ flexGrow: 1 }}>
          <Typography variant="body1" fontWeight={400} fontSize="14px">
            {params.row.code}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {params.row.name}
          </Typography>
        </Stack>
      </Stack>
    );
  }
  const listViewColDef: GridListViewColDef<ReportTemplateResponseDto> = {
    field: 'listView',
    renderCell: ListViewCell,
  };

  function SelectAllAndOrderControl() {
    if (!reportCtx) {
      return;
    }
    const selectedTemplatesSize = reportCtx.selectedTemplateIds.length;
    return (
      <div className="selector-toolbar">
        <span>
          <Checkbox
            indeterminate={selectedTemplatesSize > 0 && selectedTemplatesSize < reportTemplates.length}
            checked={selectedTemplatesSize === reportTemplates.length}
            onChange={(e) => {
              const checked = e.target.checked;
              reportCtx?.setSelectedTemplateIds(checked ? filteredTemplates.map((row) => row.id) : []);
            }}
          />
          <Typography variant="body2">
            ({filteredTemplates.length}/{reportCtx?.selectedTemplateIds.length})
          </Typography>
        </span>
        <ToolbarSortItem listView={isListView} />
      </div>
    );
  }

  function getColumnHeaders() {
    return {
      toolbar: () => (
        <Toolbar style={{ minHeight: isListView ? '120px' : '50px' }}>
          <div className="selector-header-container">
            <div>
              <SearchComponent
                searchText={searchText}
                setSearchText={setSearchText}
                showHistory={false}
                placeholder={' '}
              />
            </div>
            {isListView && <SelectAllAndOrderControl />}
          </div>
        </Toolbar>
      ),
    };
  }

  return (
    <>
      <DataGridPro
        ref={dataRef}
        rows={filteredTemplates}
        columns={columns}
        disableRowSelectionOnClick
        disableColumnMenu
        hideFooterRowCount
        listView={isListView}
        listViewColumn={listViewColDef}
        hideFooter={isListView}
        autosizeOptions={{
          columns: ['select', 'id', 'name', 'code'],
        }}
        getRowHeight={() => 'auto'}
        showToolbar
        slots={getColumnHeaders()}
        disableDensitySelector
        disableColumnFilter
        pageSizeOptions={[100]}
        localeText={{
          noRowsLabel: getNoRowsLabel(),
          footerTotalRows: t('report.total'),
        }}
        getRowClassName={
          !isListView
            ? (params: GridRowParams) =>
                reportCtx?.selectedTemplateIds.includes(params.row.id) ? 'selected-report-row' : ''
            : undefined
        }
        checkboxSelection
        rowSelectionModel={selectionModel}
        onRowSelectionModelChange={setSelectionModel}
        disableRowSelectionExcludeModel
        sx={{
          '& .MuiDataGrid-columnHeaders': {
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          },
          '& .MuiDataGrid-footerContainer': {
            boxShadow: '0 -4px 6px rgba(0, 0, 0, 0.1)',
          },
          '& .MuiDataGrid-columnHeaderTitle': {
            fontWeight: 600,
          },
          '& .MuiDataGrid-toolbar': {
            borderBottom: 'none',
            boxShadow: 'none',
          },
        }}
      />
      {isListView && reportCtx.selectedTemplateIds.length > 0 && <ReportEmailButton />}
    </>
  );
}
